import { useCallback, useContext, useEffect, useState } from "react";
import { Alert, Button, Input, Form, Select, DatePicker } from "antd";

import { AuthContext } from "../../context/AuthContext";
import { useFormContext } from "../../hooks/useFormContext";
import { calculateAge } from "../../utils/ageHelper";
import { toast } from "../../utils/toast";
import {
  inceptionDateFormRule,
  isFirstOfMonth,
} from "../../utils/systemValidation";

import {
  checkGroupPolicyNumberApi,
  getGroupApi,
} from "../../services/Group/GroupApis";
import {
  getCompanyProductsApi,
  getPlansApi,
  getProductApi,
} from "../../services/Product/ProductApis";

import type { SelectProps } from "antd";
import WizardStepLayout from "./WizardStepLayout";

interface StepProps {
  memberAge: number;
  continuation?: boolean;
  gotoStep: Function;
  current: number;
  form: any;
}

/** Resolve Product Type to plan Name for filtering (never numeric PlanCategory id). */
const resolveProductTypeName = (value: any): string => {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    const label = value.label != null ? String(value.label) : "";
    const raw = value.value != null ? String(value.value) : "";
    if (label) return label;
    if (raw && Number.isNaN(Number(raw))) return raw;
    return label || "";
  }
  const asString = String(value);
  if (!Number.isNaN(Number(asString)) && asString.trim() !== "") {
    return "";
  }
  return asString;
};

const ProductInfo: React.FC<StepProps> = ({
  memberAge,
  continuation,
  gotoStep,
  current,
  form,
}) => {
  const { setProductInfo, productInfo } = useFormContext();
  const { currentGroup, currentUser } = useContext(AuthContext);

  const [products, setProducts] = useState<any[]>([]);
  const [plans, setPlans] = useState<SelectProps["options"]>([]);
  const [productCategory, setProductCategory] = useState("");
  const [filteredProducts, setFilteredProducts] =
    useState<SelectProps["options"]>();
  const [fetchLoading, setFetchLoading] = useState(false);
  const [maxAge, setMaxAge] = useState(memberAge);
  const [variablePremium, setVariablePremium] = useState(false);
  const inceptionDate = Form.useWatch("InceptionDate", form);
  const productTypeWatched = Form.useWatch("ProductType", form);
  const dateOfBirth = Form.useWatch("DateOfBirth", form);
  const extendedAgeWatched = Form.useWatch("ExtendedAge", form);
  const productTypeName = resolveProductTypeName(productTypeWatched);
  const isOnePlus = productTypeName === "One Plus";
  const hasInvalidLegacyInceptionDate =
    inceptionDate != null && !isFirstOfMonth(inceptionDate);

  const resolveMemberAge = useCallback((): number | null => {
    if (
      memberAge != null &&
      memberAge !== "" &&
      !Number.isNaN(Number(memberAge))
    ) {
      return Number(memberAge);
    }
    return calculateAge(dateOfBirth ?? form.getFieldValue("DateOfBirth"));
  }, [memberAge, dateOfBirth, form]);

  const effectiveFilterAge = (
    category: string,
    extendedAgeValue?: number | null
  ): number | null => {
    if (
      category === "One Plus" &&
      extendedAgeValue != null &&
      extendedAgeValue !== "" &&
      !Number.isNaN(Number(extendedAgeValue))
    ) {
      return Number(extendedAgeValue);
    }
    return resolveMemberAge();
  };

  const onChangeProduct = (e: any) => {
    const category = resolveProductTypeName(e) || String(e ?? "");
    setProductCategory(category);

    if (category !== "One Plus") {
      form.setFieldsValue({ ExtendedAge: undefined });
      const age = resolveMemberAge();
      setMaxAge(age ?? memberAge);
      updateFilteredProducts(products, category, age);
      return;
    }

    const extended = Number(form.getFieldValue("ExtendedAge"));
    const age = effectiveFilterAge(category, extended);
    setMaxAge(age ?? memberAge);
    updateFilteredProducts(products, category, age);
  };

  const onChangePolicyNumber = async (e: any) => {
    const policy = e.target.value;

    if (policy && policy.length === 15) {
      try {
        const { data } = await checkGroupPolicyNumberApi(currentGroup, {
          PolicyNumber: policy,
        });

        if (!data) {
          form.setFields([
            {
              name: "PolicyNumber",
              errors: ["Policy number already exists"],
            },
          ]);
        }
      } catch (error) {
        console.error("Error checking policy number:", error);
        form.setFields([
          {
            name: "PolicyNumber",
            errors: ["Error validating policy number"],
          },
        ]);
      }
    }
  };

  const onChangeAge = (e: any) => {
    const baseAge = resolveMemberAge();
    if (!isOnePlus) {
      setMaxAge(baseAge ?? memberAge);
      updateFilteredProducts(products, productCategory, baseAge);
      return;
    }

    const age = Number(e.target.value);

    if (!age) {
      setMaxAge(baseAge ?? memberAge);
      updateFilteredProducts(products, productCategory, baseAge);
    } else {
      setMaxAge(age);
      updateFilteredProducts(products, productCategory, age);
    }
  };

  const updateFilteredProducts = (
    productList: any[],
    category: string,
    age: number | null
  ) => {
    const categoryName = resolveProductTypeName(category) || category;
    if (!categoryName) {
      setFilteredProducts([]);
      return;
    }

    const existingProductId = resolveProductId(form.getFieldValue("Product"));
    const hasValidAge = age != null && !Number.isNaN(age);
    const filtered = (Array.isArray(productList) ? productList : [])
      .filter((product: any) => {
        const planName = product?.PlanCategory?.Name;
        const minAge = Number(product?.ProductCategory?.MinAge);
        const maxProductAge = Number(product?.ProductCategory?.MaxAge);
        if (!planName || Number.isNaN(minAge) || Number.isNaN(maxProductAge)) {
          return false;
        }
        if (planName !== categoryName) return false;
        if (!hasValidAge) return true;
        return minAge <= age! && age! <= maxProductAge;
      })
      .map((product: any) => ({
        value: product.id,
        label: product.ProductName,
      }));

    // Edit: keep selected product visible even if age band would exclude it
    if (
      existingProductId &&
      !filtered.some((opt) => String(opt.value) === String(existingProductId))
    ) {
      const selected = (Array.isArray(productList) ? productList : []).find(
        (product: any) => String(product.id) === String(existingProductId)
      );
      if (selected) {
        filtered.unshift({
          value: selected.id,
          label: selected.ProductName,
        });
      }
    }

    setFilteredProducts(filtered);
  };

  const handleProduct = async (e: string) => {
    const { data } = await getProductApi(e);
    setProductInfo(data);
    if (variablePremium) {
      form.validateFields(["Premium"]).catch(() => undefined);
    }
  };

  const resolveProductId = (product: any): string | null => {
    if (product == null || product === "") return null;
    if (typeof product === "object") {
      return product.value != null
        ? String(product.value)
        : product.id != null
          ? String(product.id)
          : null;
    }
    return String(product);
  };

  useEffect(() => {
    getPlansApi()
      .then((res) => {
        const planList = Array.isArray(res?.data) ? res.data : [];
        setPlans(
          planList.map((plan: any) => ({
            label: plan.Name,
            value: plan.Name,
          })),
        );
      })
      .catch((err) => {
        console.error("Error fetching plan categories:", err);
        toast.error({
          title: "Error Loading Product Types",
          description:
            "Failed to load product types. Please contact support if this issue persists.",
        });
      });
  }, []);

  useEffect(() => {
    if (!String(currentGroup)) return;

    setFetchLoading(true);
    getCompanyProductsApi(String(currentGroup))
      .then((res) => {
        const productList = Array.isArray(res?.data) ? res.data : [];
        if (productList.length === 0) {
          setProducts([]);
          setFilteredProducts([]);
          return;
        }

        setProducts(productList);

        const existingProductId = resolveProductId(form.getFieldValue("Product"));
        if (existingProductId) {
          getProductApi(existingProductId)
            .then(({ data }) => setProductInfo(data))
            .catch((err) =>
              console.error("Error loading selected product details:", err)
            );
        }
      })
      .catch((err) => {
        console.error("Error fetching products:", err);
        toast.error({
          title: "Error Loading Products",
          description:
            "Failed to load products for this group. Please contact support if this issue persists.",
        });
      })
      .finally(() => setFetchLoading(false));

    getGroupApi(String(currentGroup)).then((res) => {
      setVariablePremium(res.data.VariablePremium);

      const currentPolicyNumber = form.getFieldValue("PolicyNumber");

      if (!currentPolicyNumber) {
        const policyNumber =
          (res.data.PolicyPrefix || "") +
          "0".repeat(
            Math.max(
              0,
              15 -
                (res.data.PolicyPrefix || "").length -
                String(res.data.PolicyIncrement || 0).length
            )
          ) +
          (res.data.PolicyIncrement || 0);

        form.setFieldsValue({ PolicyNumber: policyNumber });
      }
    });
  }, [currentGroup]);

  useEffect(() => {
    if (!products.length) return;

    const rawType = productTypeWatched ?? form.getFieldValue("ProductType");
    let selectedType = resolveProductTypeName(rawType);
    if (!selectedType) {
      selectedType = products[0]?.PlanCategory?.Name || "";
    }

    if (selectedType && typeof rawType === "object" && rawType?.value != null) {
      const numericId = !Number.isNaN(Number(rawType.value));
      if (numericId || rawType.value !== selectedType) {
        form.setFieldsValue({
          ProductType: selectedType,
        });
      }
    }

    setProductCategory(selectedType);
    const extendedNum =
      extendedAgeWatched != null && extendedAgeWatched !== ""
        ? Number(extendedAgeWatched)
        : null;
    const filterAge = effectiveFilterAge(selectedType, extendedNum);
    setMaxAge(filterAge ?? resolveMemberAge() ?? memberAge);
    updateFilteredProducts(products, selectedType, filterAge);
  }, [
    products,
    memberAge,
    dateOfBirth,
    productTypeWatched,
    extendedAgeWatched,
    resolveMemberAge,
  ]);

  const minProductPremium = Number(
    (productInfo as any)?.PolicyPremium ?? Number.NaN
  );

  const tailLayout = {
    labelCol: { span: 8 },
    wrapperCol: { offset: 8, span: 8 },
  };

  const content = (
    <WizardStepLayout
      action={
        <Form.Item {...tailLayout} style={{ marginBottom: 0 }}>
          <Button block size="large" onClick={() => gotoStep(current + 1)}>
            Next
          </Button>
        </Form.Item>
      }
    >
      {(currentUser.Role.Name === "Admin" ||
        currentUser.Role.Name === "Super Admin" ||
        currentUser.Role.Name === "Agent" ||
        currentUser.Role.Name === "Data Capturer") &&
        !continuation && (
          <Form.Item
            name="PolicyNumber"
            label="Policy Number"
            rules={[
              {
                pattern: /^.{15}$/,
                message: "Policy number must be 15 characters",
                required: true,
              },
            ]}
          >
            <Input
              maxLength={15}
              size="large"
              placeholder="Policy Number"
              onChange={(e) => onChangePolicyNumber(e)}
            />
          </Form.Item>
        )}

      <Form.Item
        name="ProductType"
        label="Product Type"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Select
          options={plans}
          size="large"
          style={{ width: "100%" }}
          placeholder="Select Product Type"
          onChange={(e) => onChangeProduct(e)}
        />
      </Form.Item>
      {isOnePlus && (
        <Form.Item
          name="ExtendedAge"
          label="Extended Age"
          extra="Only applies to One Plus. Other product types use age from the ID number."
        >
          <Input
            size="large"
            placeholder="Extended Age"
            onChange={(e) => onChangeAge(e)}
          />
        </Form.Item>
      )}
      {hasInvalidLegacyInceptionDate && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Invalid inception date"
          description="This policy has an inception date that is not the 1st of the month. Select the correct month below to fix it before saving."
        />
      )}
      <Form.Item
        name="InceptionDate"
        label="Inception Date"
        help="Inception date is always the 1st of the selected month."
        rules={[
          {
            required: true,
          },
          inceptionDateFormRule,
        ]}
      >
        <DatePicker
          picker="month"
          format="MMMM YYYY"
          placeholder="Inception Date"
          size="large"
          style={{ width: "100%" }}
          onChange={(val) => {
            if (val) {
              form.setFieldsValue({
                InceptionDate: val.startOf("month").add(2, "hour"),
              });
            }
          }}
        />
      </Form.Item>

      <Form.Item
        name="Product"
        label="Product"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Select
          loading={fetchLoading}
          onChange={(e) => handleProduct(e)}
          options={filteredProducts}
          size="large"
          style={{ width: "100%" }}
          placeholder="Select Product"
          notFoundContent={
            fetchLoading
              ? undefined
              : products.length > 0
                ? "No products match this member's age and product type"
                : "No products configured for this group"
          }
        />
      </Form.Item>

      {variablePremium && (
        <Form.Item
          name="Premium"
          label="Premium"
          rules={[
            { required: true, message: "Premium is required" },
            {
              validator: async (_, value) => {
                if (value == null || value === "") return;
                const premium = Number(value);
                if (Number.isNaN(premium)) {
                  throw new Error("Enter a valid premium amount");
                }
                if (
                  !Number.isNaN(minProductPremium) &&
                  premium < minProductPremium
                ) {
                  throw new Error(
                    `Premium cannot be lower than the product premium (R${minProductPremium})`
                  );
                }
              },
            },
          ]}
          dependencies={["Product"]}
        >
          <Input size="large" placeholder="Premium" />
        </Form.Item>
      )}
    </WizardStepLayout>
  );

  return content;
};
export default ProductInfo;
